import { createSelector } from '@spcc/core'
import type { SubCustomerBucket, SubCustomerDashboardState, SubCustomerRequestInput } from '../domain/types'
import {
  avgTonsPerOrder,
  buildFilterChips,
  countActiveFilters,
  resolveGlobalFactoryIds,
} from '../domain/utils/sub-customer-dashboard'
import { initialSubCustomerDashboardState } from './sub-customer-dashboard.slice'

// The header scope lives in other domains' slices. It is read structurally — no
// cross-domain import — the same way the Sales sagas read `state.locations`.
interface HeaderScopeState {
  adminContext?: { selectedTenant?: { documentId?: string; id?: string | number; name?: string } | null }
  locations?: {
    derivedFactoryId?: string | null
    currentLocationId?: string | null
    currentLocation?: { name?: string; factoryName?: string } | null
  }
}
export type SubCustomerRootState = HeaderScopeState & { subCustomerDashboard?: SubCustomerDashboardState }

const selectState = (state: SubCustomerRootState) => state.subCustomerDashboard ?? initialSubCustomerDashboardState

export const selectSubCustomerDateRange = createSelector(selectState, (s) => s.dateRange)
export const selectSubCustomerFilters = createSelector(selectState, (s) => s.filters)
export const selectSubCustomerOptions = createSelector(selectState, (s) => s.options)
export const selectSubCustomerOptionsKey = createSelector(selectState, (s) => s.optionsKey)
export const selectSubCustomerTop = createSelector(selectState, (s) => s.top)
export const selectSubCustomerSearch = createSelector(selectState, (s) => s.search)
export const selectSelectedSubCustomer = createSelector(selectState, (s) => s.selected)
export const selectSubCustomerDetail = createSelector(selectState, (s) => s.detail)
export const selectSubCustomerExportDialog = createSelector(selectState, (s) => s.exportDialog)
export const selectSubCustomerLoading = createSelector(selectState, (s) => s.loading)
export const selectSubCustomerError = createSelector(selectState, (s) => s.error)
export const selectSubCustomerActiveFilterCount = createSelector(selectSubCustomerFilters, countActiveFilters)
export const selectSubCustomerFilterChips = createSelector(selectSubCustomerFilters, selectSubCustomerOptions, buildFilterChips)
export const selectSubCustomerAvgTonsPerOrder = createSelector(selectSubCustomerTop, avgTonsPerOrder)

// ===== Header (global) scope =====
const selectHeaderTenant = (state: SubCustomerRootState) => state.adminContext?.selectedTenant ?? null
const selectDerivedFactoryId = (state: SubCustomerRootState) => state.locations?.derivedFactoryId ?? null
const selectCurrentLocationId = (state: SubCustomerRootState) => state.locations?.currentLocationId ?? null
const selectCurrentLocation = (state: SubCustomerRootState) => state.locations?.currentLocation ?? null

export const selectSubCustomerGlobalTenant = createSelector(selectHeaderTenant, (tenant) =>
  tenant ? { id: String(tenant.documentId ?? tenant.id), name: tenant.name ?? '' } : null,
)
export const selectSubCustomerGlobalFactoryIds = createSelector(selectDerivedFactoryId, selectCurrentLocationId, resolveGlobalFactoryIds)
export const selectSubCustomerGlobalPlantLabel = createSelector(selectCurrentLocation, (location) =>
  location ? location.factoryName ?? location.name ?? null : null,
)

/** Changes whenever the header plant or customer changes — the page re-inits on it. */
export const selectSubCustomerScopeKey = createSelector(
  selectSubCustomerGlobalTenant,
  selectSubCustomerGlobalFactoryIds,
  (tenant, factoryIds) => `${tenant?.id ?? ''}|${factoryIds?.join(',') ?? ''}`,
)

export const selectSubCustomerRequestInput = createSelector(
  selectSubCustomerDateRange,
  selectSubCustomerFilters,
  selectSubCustomerGlobalFactoryIds,
  selectSubCustomerGlobalTenant,
  (dateRange, filters, globalFactoryIds, tenant): SubCustomerRequestInput => ({
    dateRange,
    filters,
    globalFactoryIds,
    globalTenantId: tenant?.id ?? null,
  }),
)

// ===== Detail summary (derived) =====
const countKnown = (buckets: SubCustomerBucket[]) => buckets.filter((bucket) => bucket.id !== null).length

export const selectSubCustomerDetailCounts = createSelector(selectSubCustomerDetail, (detail) => ({
  cities: detail ? countKnown(detail.byCity) : 0,
  plants: detail ? countKnown(detail.byPlant) : 0,
  cementTypes: detail ? countKnown(detail.byCementType) : 0,
}))
